import { ReactNode, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

type DialogProps = Omit<React.ComponentProps<typeof Dialog>, "children">;
type ButtonProps = Omit<React.ComponentProps<typeof Button>, "children">;
type DialogContentProps = React.ComponentProps<typeof DialogContent>;
type TooltipProps = Omit<
  React.ComponentProps<typeof Tooltip>,
  "children" | "open" | "onOpenChange"
>;
type TooltipProviderProps = React.ComponentProps<typeof TooltipProvider>;
type TooltipContentProps = Omit<
  React.ComponentProps<typeof TooltipContent>,
  "children"
>;

export type DialogTooltipButtonProps = DialogProps & {
  tooltip: ReactNode;
  dialogTitle: ReactNode;
  icon?: ReactNode;
  label?: ReactNode;
  triggerContent?: ReactNode;
  content: ReactNode;
  buttonProps?: ButtonProps;
  dialogContentProps?: DialogContentProps;
  tooltipProps?: TooltipProps;
  tooltipProviderProps?: TooltipProviderProps;
  tooltipContentProps?: TooltipContentProps;
  suppressAfterCloseMs?: number;
};

/**
 * DialogTooltipButton renders one trigger button that opens a dialog and shows a tooltip on hover.
 * It closes the tooltip when the dialog opens or closes so the tooltip does not stay visible.
 *
 * Trigger content options:
 * icon only with `icon`
 * text only with `label`
 * icon plus text with `icon` and `label`
 * full custom trigger content with `triggerContent`, this overrides `icon` and `label`
 *
 * Example usage
 * <DialogTooltipButton
 *   tooltip="Element löschen"
 *   dialogTitle="Element wirklich löschen?"
 *   icon={<Trash2 className="h-4 w-4" />}
 *   label="Löschen"
 *   buttonProps={{ variant: "outline" }}
 *   content={<DialogFooter>...</DialogFooter>}
 * />
 */
export function DialogTooltipButton({
  tooltip,
  dialogTitle,
  icon,
  label,
  triggerContent,
  content,
  open,
  onOpenChange,
  buttonProps,
  dialogContentProps,
  tooltipProps,
  tooltipProviderProps,
  tooltipContentProps,
  suppressAfterCloseMs = 250,
  ...dialogProps
}: DialogTooltipButtonProps) {
  const [uncontrolledDialogOpen, setUncontrolledDialogOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? Boolean(open) : uncontrolledDialogOpen;
  const resolvedTriggerContent = triggerContent ?? (
    <>
      {icon}
      {label}
    </>
  );

  const setDialogOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledDialogOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
    setTooltipOpen(false);
    if (!nextOpen) {
      setTooltipSuppressed(true);
      window.setTimeout(
        () => setTooltipSuppressed(false),
        suppressAfterCloseMs,
      );
      triggerRef.current?.blur();
    }
  };

  return (
    <Dialog {...dialogProps} open={dialogOpen} onOpenChange={setDialogOpen}>
      <TooltipProvider {...tooltipProviderProps}>
        <Tooltip
          {...tooltipProps}
          open={tooltipOpen}
          onOpenChange={(nextOpen) => {
            if (dialogOpen || tooltipSuppressed) {
              setTooltipOpen(false);
              return;
            }
            setTooltipOpen(nextOpen);
          }}
        >
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                ref={triggerRef}
                variant={buttonProps?.variant ?? "outline"}
                size={buttonProps?.size ?? "sm"}
                {...buttonProps}
                className={cn(buttonProps?.className)}
                onClick={(event) => {
                  setTooltipOpen(false);
                  buttonProps?.onClick?.(event);
                }}
              >
                {resolvedTriggerContent}
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent {...tooltipContentProps}>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DialogContent
        {...dialogContentProps}
        className={cn(dialogContentProps?.className)}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.blur();
          dialogContentProps?.onCloseAutoFocus?.(event);
        }}
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
